import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DataGrid, { Column, Scrolling, Selection } from 'devextreme-react/data-grid'
import TextBox from 'devextreme-react/text-box'
import Button from 'devextreme-react/button'
import axios from 'axios'
import notify from '../../utils/notify'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import { confirmDialog } from '../../components'

type ConfirmDialogConfig = {
	title?: string
	message?: string
}

type ButtonConfig = {
	text?: string
	icon?: string
	confirmDialog?: ConfirmDialogConfig
}

type BulkActionPopupArgs = {
	folder?: string
	model: string
	key: string
	displayField: string
	displayCaption?: string
	execute: string
	itemLabel?: string
	successMessage?: string
	button?: ButtonConfig
	popupTitle?: string
	executeModel?: string
	executeFolder?: string
	limit?: number
}

type BulkActionPopupProps = {
	args: BulkActionPopupArgs
	filter: any
}

const getFolderFromUrl = (): string => {
	try {
		const params = new URLSearchParams(window.location.search)
		return params.get('folder') || ''
	} catch {
		return ''
	}
}

const BulkActionPopup: React.FC<BulkActionPopupProps> = ({ args, filter }) => {
	const {
		folder: folderArg,
		model,
		key,
		displayField,
		displayCaption,
		execute,
		itemLabel = 'item',
		successMessage,
		button,
		popupTitle,
		executeModel,
		executeFolder,
		limit = 1000,
	} = args

	const folder = folderArg || getFolderFromUrl()

	const [rows, setRows] = useState<any[]>([])
	const [selectedKeys, setSelectedKeys] = useState<any[]>([])
	const [searchText, setSearchText] = useState('')
	const [initialLoading, setInitialLoading] = useState(true)
	const [searching, setSearching] = useState(false)
	const [loading, setLoading] = useState(false)
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const fetchRows = useCallback(
		async (search?: string) => {
			const body: any = { skip: 0, take: limit, model }
			if (folder) body.folder = folder
			if (search?.trim()) {
				body.filter = JSON.stringify([[displayField, 'contains', search.trim()]])
			}
			const response = await axios.post(`${API_FIBER_URL}/view/data`, body)
			return Array.isArray(response.data?.data) ? response.data.data : []
		},
		[folder, model, displayField, limit]
	)

	useEffect(() => {
		let cancelled = false
		const load = async () => {
			setInitialLoading(true)
			try {
				const data = await fetchRows()
				if (!cancelled) setRows(data)
			} catch (err) {
				if (!cancelled) {
					console.error('Error loading popup data', err)
					notify('Error loading popup data', 'error')
				}
			} finally {
				if (!cancelled) setInitialLoading(false)
			}
		}
		load()
		return () => {
			cancelled = true
		}
	}, [fetchRows])

	useEffect(() => {
		return () => {
			if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
		}
	}, [])

	const handleSearchChanged = useCallback(
		(value: string) => {
			setSearchText(value)
			if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
			searchTimerRef.current = setTimeout(async () => {
				setSearching(true)
				try {
					setRows(await fetchRows(value))
				} catch (err) {
					console.error('Error searching popup data', err)
				} finally {
					setSearching(false)
				}
			}, 350)
		},
		[fetchRows]
	)

	const sortedRows = useMemo(() => {
		const selectedSet = new Set(selectedKeys)
		return [...rows].sort((a, b) => {
			const aSelected = selectedSet.has(a[key]) ? 0 : 1
			const bSelected = selectedSet.has(b[key]) ? 0 : 1
			return aSelected - bSelected
		})
	}, [rows, selectedKeys, key])

	const handleSelectionChanged = useCallback((e: any) => {
		setSelectedKeys(e.selectedRowKeys)
	}, [])

	const handleAction = useCallback(async () => {
		if (selectedKeys.length === 0) {
			notify(`Please select at least one ${itemLabel}.`, 'warning')
			return
		}

		const cd = button?.confirmDialog
		if (cd) {
			const confirmed = await confirmDialog(
				cd.title || 'Confirm Action',
				cd.message || `You are about to perform this action on ${selectedKeys.length} ${itemLabel}(s). Proceed?`
			)
			if (!confirmed) return
		}

		setLoading(true)
		try {
			await axios.post(`${API_FIBER_URL}/popup/execute`, {
				filter,
				model: executeModel || model,
				folder: executeFolder || folder,
				execute,
				selected_keys: selectedKeys,
			})
			notify(successMessage || `${itemLabel}s updated successfully.`, 'success')
			setSelectedKeys([])
			setRows(await fetchRows(searchText))
		} catch (error: any) {
			const errorMsg =
				error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error'
			console.error('BulkActionPopup error:', error.response?.data || error)
			notify(`Error: ${errorMsg}`, 'error')
		} finally {
			setLoading(false)
		}
	}, [
		filter,
		model,
		folder,
		execute,
		executeModel,
		executeFolder,
		selectedKeys,
		itemLabel,
		successMessage,
		button,
		fetchRows,
		searchText,
	])

	return (
		<div className="widget-container">
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
				<p style={{ margin: 0, fontWeight: 'bold', fontSize: 20 }}>
					{popupTitle || displayCaption || displayField}
				</p>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					{(initialLoading || loading || searching) && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
							<span style={{ fontSize: 12, color: '#999' }}>
								{initialLoading ? 'Loading...' : loading ? 'Applying...' : 'Searching...'}
							</span>
							<LoadIndicator height={16} width={16} />
						</div>
					)}
					{!initialLoading && (
						<TextBox
							placeholder="Search..."
							value={searchText}
							onValueChanged={e => handleSearchChanged(e.value ?? '')}
							showClearButton={true}
							stylingMode="underlined"
							width={280}
							height={28}
						/>
					)}
				</div>
			</div>

			{!initialLoading && (
				<>
					<DataGrid
						keyExpr={key}
						dataSource={sortedRows}
						height="50vh"
						showBorders={true}
						columnAutoWidth={true}
						rowAlternationEnabled={true}
						hoverStateEnabled={true}
						loadPanel={{ enabled: false }}
						selectedRowKeys={selectedKeys}
						onSelectionChanged={handleSelectionChanged}
					>
						<Scrolling mode="virtual" />
						<Selection mode="multiple" showCheckBoxesMode="always" selectAllMode="allPages" />
						<Column dataField={displayField} caption={displayCaption || displayField} />
					</DataGrid>

					<div
						style={{
							marginTop: 12,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<span style={{ fontSize: 13, color: '#666' }}>
							{selectedKeys.length} {itemLabel}
							{selectedKeys.length !== 1 ? 's' : ''} selected
						</span>
						<Button
							text={button?.text || 'Apply'}
							icon={button?.icon || 'check'}
							type="default"
							disabled={loading || selectedKeys.length === 0}
							onClick={handleAction}
						/>
					</div>
				</>
			)}
		</div>
	)
}

export default BulkActionPopup
